// Racket Lab — Mini navigation header (R4.1-B, Feb 2026).
//
// Top-of-module tabs that contextualise where the user is INSIDE Racket Lab.
// Tabs that are not yet implemented are rendered disabled with a "Coming soon"
// tooltip — never broken navigation. Visibility per role:
//
//   • Tickets       — everyone authenticated
//   • Calendar      — everyone authenticated (R4.1-B mini-widget)
//   • Players       — technician + admin/staff only
//   • Technicians   — admin/staff only
//
// We intentionally avoid hiding tabs entirely — disabled+tooltip is more
// informative for the stakeholder demo than a tab that silently vanishes.

import React from "react";
import { NavLink } from "react-router-dom";
import { Ticket, CalendarDays, Users, Wrench } from "lucide-react";

const ADMIN_ROLES = new Set(["admin", "staff"]);
const TECH_ROLES = new Set(["admin", "staff"]); // techs gated by profile_key

function _canSeePlayersTab(user) {
  if (!user) return false;
  return TECH_ROLES.has(user.role) || user.profile_key === "racket_technician";
}

function _canSeeTechniciansTab(user) {
  return !!user && ADMIN_ROLES.has(user.role);
}

const _baseClass =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors";
const _activeClass = "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
const _idleClass = "text-slate-600 hover:bg-slate-50";
const _disabledClass = "text-slate-300 cursor-not-allowed";

export default function RacketLabNav({ currentUser, t }) {
  const playersEnabled = _canSeePlayersTab(currentUser);
  const technsEnabled = _canSeeTechniciansTab(currentUser);

  return (
    <nav
      className="flex items-center gap-1 flex-wrap border border-slate-200 rounded-xl bg-white p-1.5"
      data-testid="racket-lab-nav"
      aria-label="Racket Lab navigation"
    >
      <span className="text-xs font-semibold text-slate-400 px-2 hidden sm:inline">
        {t("racketLab.nav.label")}
      </span>

      <NavLink
        to="/app/racket-lab/tickets"
        end
        className={({ isActive }) =>
          `${_baseClass} ${isActive ? _activeClass : _idleClass}`
        }
        data-testid="racket-lab-nav-tickets"
      >
        <Ticket className="h-3.5 w-3.5" />
        {t("racketLab.nav.tickets")}
      </NavLink>

      <NavLink
        to="/app/racket-lab/calendar"
        className={({ isActive }) =>
          `${_baseClass} ${isActive ? _activeClass : _idleClass}`
        }
        data-testid="racket-lab-nav-calendar"
      >
        <CalendarDays className="h-3.5 w-3.5" />
        {t("racketLab.nav.calendar")}
      </NavLink>

      {playersEnabled ? (
        <span
          className={`${_baseClass} ${_disabledClass}`}
          title={t("racketLab.nav.comingSoon")}
          data-testid="racket-lab-nav-players-disabled"
        >
          <Users className="h-3.5 w-3.5" />
          {t("racketLab.nav.players")} · {t("racketLab.nav.comingSoon")}
        </span>
      ) : (
        <span
          className={`${_baseClass} ${_disabledClass}`}
          title={t("racketLab.nav.insufficientRole")}
          data-testid="racket-lab-nav-players-blocked"
        >
          <Users className="h-3.5 w-3.5" />
          {t("racketLab.nav.players")}
        </span>
      )}

      {technsEnabled ? (
        <span
          className={`${_baseClass} ${_disabledClass}`}
          title={t("racketLab.nav.comingSoon")}
          data-testid="racket-lab-nav-technicians-disabled"
        >
          <Wrench className="h-3.5 w-3.5" />
          {t("racketLab.nav.technicians")} · {t("racketLab.nav.comingSoon")}
        </span>
      ) : (
        <span
          className={`${_baseClass} ${_disabledClass}`}
          title={t("racketLab.nav.insufficientRole")}
          data-testid="racket-lab-nav-technicians-blocked"
        >
          <Wrench className="h-3.5 w-3.5" />
          {t("racketLab.nav.technicians")}
        </span>
      )}
    </nav>
  );
}
